import { useRef, useEffect } from 'react';
import type { RefObject } from 'react';
import type { Price, SelectedPrice } from '@printdeal/product-selector';
import { scrollIntoView } from '@printdeal/ui-library';
import type { SelectedDeliveryTool } from '../types/file-delivery/FileDelivery';

interface ScrollRefs {
  deliveryChoiceRef: RefObject<HTMLDivElement>;
  summaryRef: RefObject<HTMLDivElement>;
  fileCheckSelectionRef: RefObject<HTMLDivElement>;
}

interface Props {
  selectedPrice: SelectedPrice | Price | undefined;
  selectedDeliveryTool?: SelectedDeliveryTool;
  isExp1862Variant?: boolean;
  alternativeUploadService?: boolean;
  isExp1853Variant?: boolean;
}

/**
 * Scroll to the delivery choice or summary section when a price is selected.
 * @param selectedPrice
 */
export const useScrollToSummary = ({
  selectedPrice,
  selectedDeliveryTool,
  isExp1862Variant,
  alternativeUploadService,
  isExp1853Variant,
}: Props): ScrollRefs => {
  const deliveryChoiceRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const fileCheckSelectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedPrice) {
      if ((isExp1862Variant && !selectedDeliveryTool && deliveryChoiceRef.current) || deliveryChoiceRef.current) {
        scrollIntoView(deliveryChoiceRef.current, { behavior: 'smooth', block: 'center' });
      }
      if (isExp1862Variant && selectedDeliveryTool && !alternativeUploadService && fileCheckSelectionRef.current) {
        scrollIntoView(fileCheckSelectionRef.current, { behavior: 'smooth', block: 'center' });
      }
      if (summaryRef.current && !isExp1853Variant) {
        scrollIntoView(summaryRef.current, { behavior: 'smooth', block: 'center' });
      }
    }
  }, [alternativeUploadService, isExp1862Variant, selectedDeliveryTool, selectedPrice, isExp1853Variant]);

  return { deliveryChoiceRef, summaryRef, fileCheckSelectionRef };
};
